import React, {CSSProperties, useMemo} from 'react';
import {ThumbnailProps} from './MediaGalleryThumbnailFactory';
import ThumbnailBase from './ThumbnailBase';
import {useTranslation} from 'react-i18next';
import {calcRotation, flipSize} from '../../../Utils/Asset';
import classNames from 'classnames';
import {Theme, ThemeName} from '@taopix/taopix-design-system';
import {SelectionMode} from '../MediaGallery';
import {Common} from '@taopix/taopix-core';

const ThumbnailDetailed = ({
  image,
  openDetailsDialog,
  used,
  onSelectPicture,
  selectableImages,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchEnd,
  onDeleteAsset,
  selected,
  localeString,
  currencyCode,
  expired,
  showDeleteButton = false,
  showPricing,
  showUsage,
  showLabel,
  selectionAppearance,
  disabled,
  columns,
  overlayDisplayMode,
  maxSelectionReached = false,
  selectionMode = SelectionMode.Multi,
}: ThumbnailProps) => {
  const {t} = useTranslation(['picturemanager']);
  // if the image is not processed, ignore rotation
  const rotation = useMemo(() => {
    return calcRotation(image);
  }, [image]);

  const rotatedStyles = useMemo(() => {
    return {
      position: 'absolute',
      transform: `rotate(${rotation}deg)`,
    };
  }, [rotation]) as CSSProperties;

  const sizing = flipSize(rotation, {width: image.width, height: image.height});

  const cssProperties = {
    '--asset-width': sizing.width,
    '--asset-height': sizing.height,
    '--columns': columns,
  } as React.CSSProperties;

  const containerClasses = classNames(
    'group/thumbnail',
    'flex',
    'flex-wrap',
    'relative',
    'overflow-hidden',
    ...(columns ? ['w-[calc((100%-(var(--grid-gap)*var(--columns)))/var(--columns))]'] : ['w-full'])
  );

  const imageBoxClasses = classNames(
    'relative',
    'overflow-hidden',
    'flex',
    'justify-center',
    'items-center',
    'self-start',
    'p-xs',
    'w-full',
    'before:pt-[100%]',
    'max-w-full',
    'max-h-full',
    'rounded-[min(theme(borderRadius.themeCornerSize),5rem)]'
  );

  const imageClasses = classNames(
    'max-w-full',
    'max-h-full',
    'm-auto',
    'top-0',
    'bottom-0',
    'left-0',
    'right-0'
  );

  const detailsPanelClasses = classNames(
    'min-w-[60rem]',
    'mx-sm',
    'text-sm',
    'flex-1',
    'self-start',
    'mb-lg',
    'mt-xs',
    'break-words'
  );

  const filnameClasses = classNames('block', 'text-sm');

  const fileInfoClasses = classNames('block', 'text-sm', 'opacity-50');

  //TODO: Needs to retrieve format from the format.conf?
  const formatDate = (inDate: string): string => {
    const langCode = (localeString as string).startsWith('zh')
      ? (localeString as string).split('_').join('-')
      : localeString;
    return new Date(inDate).toLocaleDateString(langCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <li className={containerClasses} style={cssProperties}>
      <Theme
        name={ThemeName.Container}
        className={imageBoxClasses}
        allowBorder={false}
        allowCorners={false}
      >
        <ThumbnailBase
          currencyCode={currencyCode}
          disabled={disabled}
          expired={expired}
          image={image}
          imageClasses={imageClasses}
          imageCSS={rotatedStyles}
          localeString={localeString}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onSelectPicture={onSelectPicture}
          onTouchEnd={onTouchEnd}
          onTouchStart={onTouchStart}
          onDeleteAsset={onDeleteAsset}
          openDetailsDialog={openDetailsDialog}
          selectionAppearance={selectionAppearance}
          selectableImages={selectableImages}
          selected={selected}
          showDeleteButton={showDeleteButton}
          showPricing={showPricing}
          showUsage={showUsage}
          showLabel={showLabel}
          used={used}
          overlayDisplayMode={overlayDisplayMode}
          maxSelectionReached={maxSelectionReached}
          selectionMode={selectionMode}
          thumbnailLayout="details-list"
        />
      </Theme>
      <div className={detailsPanelClasses}>
        <span className={filnameClasses} draggable={true}>
          {Common.getCurrentLocaleString(image?.name || '', true)}
        </span>
        <span className={fileInfoClasses}>
          {formatDate(image?.fileDateModified as unknown as string)}
        </span>
        {showUsage && (
          <span className={fileInfoClasses}>
            {`${t('str_LabelUsed', {ns: 'picturemanager'})} ${used}`}
          </span>
        )}
      </div>
    </li>
  );
};

export default ThumbnailDetailed;
